import { useCallback, useEffect, useMemo, useState } from 'react';
import { computeExpectedCash, toDatetimeLocalValue } from './formatters';
export function useSellCreditsCashForm({ buyerCostRate, open, flow }) {
    const [creditAmount, setCreditAmount] = useState('');
    const [cashReceived, setCashReceived] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [receivedAt, setReceivedAt] = useState(() => toDatetimeLocalValue(new Date()));
    const [externalReference, setExternalReference] = useState('');
    const [note, setNote] = useState('');
    useEffect(() => {
        if (!open)
            return;
        setCreditAmount('');
        setCashReceived('');
        setPaymentMethod('cash');
        setReceivedAt(toDatetimeLocalValue(new Date()));
        setExternalReference('');
        setNote('');
    }, [open]);
    const creditsNum = useMemo(() => {
        const n = Number(creditAmount);
        return Number.isFinite(n) ? n : 0;
    }, [creditAmount]);
    const expectedCash = useMemo(() => computeExpectedCash(creditsNum, buyerCostRate), [creditsNum, buyerCostRate]);
    useEffect(() => {
        // V1 (no debt): cash received is locked to expected cash.
        if (creditsNum > 0 && expectedCash > 0)
            setCashReceived(String(expectedCash));
        else
            setCashReceived('');
    }, [expectedCash, creditsNum]);
    const cashReceivedNum = useMemo(() => {
        const n = Number(cashReceived);
        return Number.isFinite(n) ? n : 0;
    }, [cashReceived]);
    const validationError = useMemo(() => {
        if (!creditsNum || creditsNum <= 0)
            return 'Enter a valid credit amount';
        if (!buyerCostRate || buyerCostRate <= 0) {
            return flow === 'shop_player_deposit'
                ? 'Face value must be positive'
                : 'Buyer cost rate is invalid';
        }
        if (!cashReceivedNum || cashReceivedNum <= 0)
            return 'Enter cash received';
        if (!receivedAt)
            return 'Received at is required';
        return null;
    }, [creditsNum, buyerCostRate, cashReceivedNum, receivedAt, flow]);
    const buildSubmitValues = useCallback(() => {
        if (validationError)
            return null;
        return {
            creditAmount: creditsNum,
            cashReceived: cashReceivedNum,
            paymentMethod,
            receivedAt: new Date(receivedAt).toISOString(),
            externalReference: externalReference.trim() || undefined,
            note: note.trim() || undefined,
        };
    }, [
        validationError,
        creditsNum,
        cashReceivedNum,
        paymentMethod,
        receivedAt,
        externalReference,
        note,
    ]);
    return {
        creditAmount,
        setCreditAmount,
        cashReceived,
        setCashReceived,
        paymentMethod,
        setPaymentMethod,
        receivedAt,
        setReceivedAt,
        externalReference,
        setExternalReference,
        note,
        setNote,
        creditsNum,
        expectedCash,
        cashReceivedNum,
        validationError,
        buildSubmitValues,
    };
}
